import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { DSButton } from "@components/design-system/molecules";
import { DSModal } from "@components/design-system/organisms";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { isMCloudEmbedded } from "@src/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { useNewAudioNotification } from "../../hooks/use-new-audio-notification";
import { APPEND_RECORDING } from "./edit/api/mutations";
import { EditableNote } from "./edit/EditableNote";
import { GroupedNoteHeader } from "./edit/GroupedNoteHeader";
import { submitTestingSampleAudio } from "../../api/UploadAudio";
import { isTestEnv, isScribenoteEmail } from "../../constants";
import { NOTE_FIELDS } from "../../graphql/fragments/note";
import { NOTE_GROUP_FIELDS } from "../../graphql/fragments/noteGroup";
import { RETRY_FAILED_AUTO_SOAP } from "../../graphql/mutations/RetryFailedAutoSoap";
import withTemplatesMessageModal from "../../hocs/withTemplatesMessageModal";
import { useTestUI } from "../../hooks/useTestUI";
import { alert } from "../common/Alert";
import { Banner } from "../common/Banner";
import {
  testRecordingsData,
  TestRecordTypeDropdown,
  SubmitTestRecordingButton,
} from "../dashboard/TestRecordings";
import PageTitle from "../layout/PageTitle";

export const GET_NOTE = gql`
  ${NOTE_FIELDS}
  ${NOTE_GROUP_FIELDS}
  query getNote($uuid: String) {
    note(uuid: $uuid) {
      uuid
      user {
        uuid
        shouldUseAutoSoaps
        roleId
        firstName
        lastName
        account {
          uuid
          headerInfoPdf
          logoFileLink
        }
      }
      patient {
        uuid
        name
        lastNameOfOwner
        sex
        species
      }
      autoTemplates {
        uuid
        name
        dentalChart
      }
      ...NoteFields
      attachments {
        uuid
        fileName
        fileLink
        contentType
        createdAt
      }
      keyHistory
      keyMedications
      vaccinationStatus
      audioStorageLink
      isAutoSoap
      isIncomplete
      isUnacceptable
      isHallucinating
      dataExtraction
      thumbsUp
      feedback
      timeToProcess
      isFree
      noteGroup {
        ...NoteGroupFields
      }
    }
  }
`;

const CHECKOUT_NOTE = gql`
  mutation checkoutNote($uuid: String) {
    checkoutNote(noteUuid: $uuid) {
      ok
      user {
        uuid
        firstName
        lastName
      }
    }
  }
`;

const CHECKIN_NOTE = gql`
  mutation checkinNote($uuid: String) {
    checkinNote(noteUuid: $uuid) {
      ok
    }
  }
`;

const TestFunctionality = ({ note }) => {
  const [selectedTestRecordType, setSelectedTestRecordType] =
    useState("medical_record");
  const { isTestUIEnabled } = useTestUI();

  const [appendRecording] = useMutation(APPEND_RECORDING);

  if (!note?.user || !note?.uuid) {
    return null;
  }
  const createNewNote = async (
    audioStorageLink,
    storageObjectName,
  ) => {
    return await appendRecording({
      variables: {
        noteUuid: note.uuid,
        storageObjectName: storageObjectName,
        createdAt: moment().format(),
        lastUpdatedAt: moment().format(),
      },
    })
      .then(() => {
        alert("success", "Recording has been appended!");
        setTimeout(function () {
          window.location.reload();
        }, 1000);
      })
      .catch((e) => {
        alert("error", e.message);
      });
  };

  const appendRecordingToNote = () => {
    let recording =
      testRecordingsData.find(
        (recording) => recording.value == selectedTestRecordType,
      ) || testRecordingsData[0];

    const { audioUrl } = recording;

    submitTestingSampleAudio({
      createNewNote,
      audioUrl,
    });
  };

  if (isMCloudEmbedded()) {
    return null;
  }

  if (
    !(isTestEnv || isScribenoteEmail(note.user?.email)) ||
    !isTestUIEnabled
  ) {
    return null;
  }
  return (
    <div className="flex flex-col">
      <div className="flex flex-row">
        <TestRecordTypeDropdown
          selectedTestRecordType={selectedTestRecordType}
          setSelectedTestRecordType={setSelectedTestRecordType}
          testRecordings={testRecordingsData}
        />

        <SubmitTestRecordingButton
          onClick={appendRecordingToNote}
          text={"Append Selected"}
        />
      </div>
      <span className="text-xs text-gray-500 font-italic">
        You can show/hide the test UI by clicking on the footer.
      </span>
    </div>
  );
};

function EditNote() {
  const { releaseGlobalGroupedNotes } = useFlags();
  const [checkoutNote] = useMutation(CHECKOUT_NOTE);
  const [checkinNote] = useMutation(CHECKIN_NOTE);
  const [shouldShowTranscriptDrawer, setShouldShowTranscriptDrawer] =
    useState(false);
  const [checkoutConflict, setCheckoutConflict] = useState(null);

  const [retryFailedAutoSoap] = useMutation(RETRY_FAILED_AUTO_SOAP);

  const navigate = useNavigate();

  const { noteUUID } = useParams();

  useEffect(() => {
    const doCheckout = async () => {
      const { data } = await checkoutNote({
        variables: {
          uuid: noteUUID,
        },
      });
      if (data?.checkoutNote?.ok == "false") {
        setCheckoutConflict(data.checkoutNote);
        mixpanel.track("Checkout Conflict Shown", {
          noteUuid: noteUUID,
          checkedOutBy: data.checkoutNote?.user?.uuid,
        });
      }
    };
    doCheckout();
    return () => {
      checkinNote({
        variables: {
          uuid: noteUUID,
        },
      });
    };
  }, [checkinNote, checkoutNote, noteUUID]);

  const {
    data,
    previousData,
    loading,
    error,
    refetch: refetchNote,
  } = useQuery(GET_NOTE, {
    variables: { uuid: noteUUID },
    fetchPolicy: "no-cache",
  });

  const {
    hasNewAudio,
    noteUuidsWithNewAudio,
    handleReprocessWithNewAudio,
    handleDismiss: handleDismissNewAudio,
  } = useNewAudioNotification(data?.note ?? null, refetchNote);

  if (error) {
    alert("error", error.message);
  }

  // When switching between grouped notes, keep the tab header visible
  // and show a spinner instead of blanking the page.
  const prevNoteGroup = previousData?.note?.noteGroup;
  if (!data || loading) {
    if (releaseGlobalGroupedNotes && prevNoteGroup) {
      return (
        <>
          <PageTitle title="Edit Note" alignChildren="left" />
          <GroupedNoteHeader
            noteGroup={prevNoteGroup}
            activeNoteUuid={noteUUID}
            noteUuidsWithNewAudio={noteUuidsWithNewAudio}
          />
          <div className="flex items-center justify-center py-xl">
            <FontAwesomeIcon
              icon={faSpinner}
              className="animate-spin text-brand text-2xl"
            />
          </div>
        </>
      );
    }
    return null;
  }

  function renderBanner() {
    if (data?.note?.isAutoSoap && data?.note?.isHallucinating) {
      return (
        <Banner isWarning>
          This note may need a quick review. The transcript for this
          visit looks incomplete, which can sometimes lead to missing
          or inaccurate details in the note. Please click{" "}
          <span
            role="button"
            tabIndex={0}
            className="underline font-semibold cursor-pointer"
            onClick={() => setShouldShowTranscriptDrawer(true)}
          >
            Show Transcript
          </span>{" "}
          to review the transcript. If everything looks accurate,
          safely ignore this message.
        </Banner>
      );
    } else {
      return null;
    }
  }

  const handleRetryFailedAutoSoap = async () => {
    try {
      await retryFailedAutoSoap({
        variables: {
          noteUuid: noteUUID,
          jobType: data?.note?.jobType,
          templateUuid: data?.note?.autoTemplates?.uuid,
        },
      });
      alert("warning", "Submitting as Automatic Note...");
      mixpanel.track("Note reprocessed", {
        noteUuid: noteUUID,
        jobType: data?.note?.jobType,
        templateUuid: data?.note?.autoTemplates?.uuid,
        source: "Note failed banner",
      });
      navigate("/dashboard");
    } catch (e) {
      mixpanel.track("Failed to reprocess note", {
        noteUuid: noteUUID,
        jobType: data?.note?.jobType,
        templateUuid: data?.note?.autoTemplates?.uuid,
        source: "Note failed banner",
        error: e.message,
      });
      alert("error", e.message);
    }
  };

  function hideCheckoutModal() {
    mixpanel.track("Checkout Conflict Action", {
      noteUuid: noteUUID,
      action: "proceed",
    });
    setCheckoutConflict(null);
  }

  function handleCheckoutCancel() {
    mixpanel.track("Checkout Conflict Action", {
      noteUuid: noteUUID,
      action: "cancel",
    });
    navigate("/dashboard");
  }

  return (
    <>
      <DSModal
        isOpen={!!checkoutConflict}
        onClose={hideCheckoutModal}
        title="Edit this note?"
        actions={[
          {
            label: "Cancel",
            variant: "cancel",
            onClick: handleCheckoutCancel,
          },
          {
            label: "Yes",
            variant: "primary",
            onClick: hideCheckoutModal,
            dataTestId: "confirm-button",
          },
        ]}
        dataTestId="checkout-conflict-modal"
      >
        <h5>
          Are you sure you&apos;d like to edit this note? It is
          currently checked out by:{" "}
          <strong>
            {checkoutConflict?.user?.firstName}{" "}
            {checkoutConflict?.user?.lastName}
          </strong>
          . WARNING: You may lose your changes.
        </h5>
      </DSModal>

      <TestFunctionality note={data?.note} />
      <PageTitle title="Edit Note" alignChildren="left"></PageTitle>

      {releaseGlobalGroupedNotes && data?.note?.noteGroup && (
        <GroupedNoteHeader
          noteGroup={data.note.noteGroup}
          activeNoteUuid={noteUUID}
          noteUuidsWithNewAudio={noteUuidsWithNewAudio}
        />
      )}

      {releaseGlobalGroupedNotes &&
        hasNewAudio &&
        data?.note?.noteGroup && (
          <Banner
            isWarning
            setIsPermanentlyHidden={handleDismissNewAudio}
          >
            New audio has been added to this grouped recording.
            Reprocess to update this note.{" "}
            <DSButton
              onClick={handleReprocessWithNewAudio}
              dataTestId="reprocess-new-audio"
            >
              Reprocess Note
            </DSButton>
          </Banner>
        )}

      {renderBanner()}

      {!data?.note?.isTagged &&
        !data?.note?.isApproved &&
        !data?.note?.isAutoSoap &&
        data?.note?.audioStorageLink &&
        !!data?.note?.user?.shouldUseAutoSoaps && (
          <div>
            <Banner isWarning>
              You can turn this recording into a note automatically!{" "}
              <DSButton onClick={handleRetryFailedAutoSoap}>
                Generate Note
              </DSButton>
            </Banner>
          </div>
        )}
      <EditableNote
        note={data?.note}
        refetchNote={refetchNote}
        shouldShowTranscriptDrawer={shouldShowTranscriptDrawer}
        setShouldShowTranscriptDrawer={setShouldShowTranscriptDrawer}
      />
    </>
  );
}

export default withTemplatesMessageModal(EditNote);
